import React, { useEffect, useRef, useState, useCallback } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';
import { Icon, Button } from '../ui';
import { NAV, LANGUAGES } from '../../data/nav';
import { changeAppLanguage } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../theme/ThemeContext';
import { trackLanguageSwitch, trackThemeToggle } from '../../utils/analytics';

const STAFF_ROLES = ['admin', 'manager', 'entry_operator', 'field_staff', 'hotel_manager'];

export default function Header({ onOpenDrawer }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { mode, toggleTheme } = useTheme();
  const [open, setOpen] = useState(null); // nav item id | 'lang' | 'account' | null
  const ref = useRef(null);
  const hoverTimer = useRef(null);

  const close = useCallback(() => setOpen(null), []);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open, close]);

  // Close on navigation; open the language menu when asked via #language
  useEffect(() => {
    if (location.hash === '#language') {
      setOpen('lang');
      navigate({ pathname: location.pathname, search: location.search, hash: '' }, { replace: true });
    } else {
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash]);

  const enter = (id) => { clearTimeout(hoverTimer.current); setOpen(id); };
  const leave = () => { hoverTimer.current = setTimeout(close, 140); };

  const setLang = (code) => { changeAppLanguage(code); trackLanguageSwitch(code); close(); };
  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];
  const role = user?.role?.name;
  const displayName = user?.firstName || user?.name || t('My Account');

  const handleLogout = () => { logout(); close(); navigate('/'); };

  return (
    <header className="site-header" ref={ref}>
      <div className="container site-header__inner">
        <Logo />

        <nav className="nav" aria-label="Primary">
          {NAV.map((item) => (
            item.children ? (
              <div
                key={item.id}
                className={`nav__item has-menu${open === item.id ? ' is-open' : ''}`}
                onMouseEnter={() => enter(item.id)}
                onMouseLeave={leave}
              >
                <button
                  type="button"
                  className={`nav__link${item.children.some((c) => location.pathname.startsWith(c.to)) ? ' is-active' : ''}`}
                  aria-haspopup="menu"
                  aria-expanded={open === item.id}
                  onClick={() => setOpen(open === item.id ? null : item.id)}
                >
                  {t(item.label)}<Icon name="chevron-down" size={14} className="nav__caret" />
                </button>
                <div className="nav__menu" role="menu" aria-label={t(item.label)}>
                  {item.children.map((c) => (
                    <Link key={c.to} to={c.to} role="menuitem" className="nav__menu-link" onClick={close}>
                      <span className="nav__menu-icon"><Icon name={c.icon} size={18} /></span>
                      <span>
                        <span className="nav__menu-title">{t(c.label)}</span>
                        <span className="nav__menu-desc">{t(c.desc)}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <NavLink
                key={item.id}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `nav__link${isActive || (item.id === 'blog' && location.pathname.startsWith('/news')) ? ' is-active' : ''}`}
              >
                {t(item.label)}
              </NavLink>
            )
          ))}
        </nav>

        <div className="site-header__actions">
          {/* Language */}
          <div className={`menu-wrap${open === 'lang' ? ' is-open' : ''}`}>
            <button
              type="button"
              className="icon-btn icon-btn--label"
              aria-haspopup="menu"
              aria-expanded={open === 'lang'}
              aria-label={t('Change language')}
              onClick={() => setOpen(open === 'lang' ? null : 'lang')}
            >
              <Icon name="globe" size={18} />
              <span className="icon-btn__text">{currentLang.label}</span>
              <Icon name="chevron-down" size={14} />
            </button>
            <div className="menu" role="menu">
              {LANGUAGES.map((l) => (
                <button key={l.code} type="button" role="menuitemradio" aria-checked={l.code === currentLang.code} className={`menu__item${l.code === currentLang.code ? ' is-active' : ''}`} onClick={() => setLang(l.code)}>
                  <span lang={l.code}>{l.label}</span>
                  {l.code === currentLang.code && <Icon name="check" size={14} />}
                </button>
              ))}
            </div>
          </div>

          {/* Theme */}
          <button
            type="button"
            className="icon-btn"
            aria-label={mode === 'dark' ? t('Switch to light mode') : t('Switch to dark mode')}
            title={mode === 'dark' ? t('Switch to light mode') : t('Switch to dark mode')}
            onClick={() => { trackThemeToggle(mode === 'dark' ? 'light' : 'dark'); toggleTheme(); }}
          >
            <Icon name={mode === 'dark' ? 'sun' : 'moon'} size={18} />
          </button>

          {/* Account */}
          {isAuthenticated ? (
            <div className={`menu-wrap${open === 'account' ? ' is-open' : ''}`}>
              <Button size="sm" icon={<Icon name="user" size={16} />} iconPosition="left" aria-haspopup="menu" aria-expanded={open === 'account'} onClick={() => setOpen(open === 'account' ? null : 'account')} className="account-btn">
                <span className="account-btn__name">{displayName}</span>
              </Button>
              <div className="menu menu--account" role="menu">
                <div className="menu__head">
                  <div className="menu__head-name">{user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.name || t('My Account')}</div>
                  <div className="menu__head-mail">{user?.email}</div>
                </div>
                <Link to="/profile" role="menuitem" className="menu__item" onClick={close}><Icon name="user" size={16} />{t('Profile')}</Link>
                <Link to="/profile#bookings" role="menuitem" className="menu__item" onClick={close}><Icon name="bookmark" size={16} />{t('My Bookings')}</Link>
                {STAFF_ROLES.includes(role) && <Link to="/staff" role="menuitem" className="menu__item" onClick={close}><Icon name="layout-dashboard" size={16} />{t('Staff Portal')}</Link>}
                {role === 'admin' && <Link to="/admin" role="menuitem" className="menu__item" onClick={close}><Icon name="settings" size={16} />{t('Admin')}</Link>}
                <button type="button" role="menuitem" className="menu__item menu__item--danger" onClick={handleLogout}><Icon name="log-out" size={16} />{t('Sign out')}</button>
              </div>
            </div>
          ) : (
            <Button size="sm" to="/login" icon={<Icon name="user" size={16} />} iconPosition="left" className="account-btn">
              <span className="account-btn__name">{t('My Account')}</span>
            </Button>
          )}

          {/* Mobile hamburger */}
          <button type="button" className="icon-btn icon-btn--burger" aria-label={t('Open menu')} onClick={onOpenDrawer}>
            <Icon name="menu" size={22} />
          </button>
        </div>
      </div>
    </header>
  );
}
